import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { Box, Button, Paper, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import { Component } from 'react'
import { errorService } from '@/data/services/error/errorService'
import { getCardStyles } from '../../styles/themes/themeExtensions'

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo })
    errorService.logError(error, errorInfo)
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })

    if (this.props.onReset) {
      this.props.onReset()
    }
  }

  render() {
    const { hasError, error } = this.state
    const { fallback, children } = this.props

    if (hasError) {
      if (fallback) {
        return fallback(error, this.handleReset)
      }

      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            minHeight: '200px',
            p: 3,
          }}
        >
          <Paper
            elevation={0}
            sx={(theme) => ({
              ...getCardStyles(theme).root,
              height: 'auto',
              p: 4,
              maxWidth: '500px',
              width: '100%',
            })}
          >
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <ErrorOutlineIcon color='error' sx={{ fontSize: 60 }} />
            </Box>

            <Typography variant='h5' component='h2' gutterBottom align='center'>
              Something went wrong
            </Typography>

            {import.meta.env.MODE !== 'production' && error && (
              <Typography
                variant='body2'
                color='error.main'
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: 'background.default',
                  border: 1,
                  borderColor: 'divider',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {error.toString()}
              </Typography>
            )}

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button variant='contained' color='primary' onClick={this.handleReset}>
                Try again
              </Button>
            </Box>
          </Paper>
        </Box>
      )
    }

    return children
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.func,
  onReset: PropTypes.func,
}
